import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API, eur, gr } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowsClockwise, FileText, PaperPlaneTilt } from "@phosphor-icons/react";
import { SignaturePad } from "@/components/SignaturePad";

export default function Renewals() {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["contracts"],
    queryFn: async () => (await api.get("/contracts")).data,
  });
  const [sel, setSel] = useState(null);
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [managerSig, setManagerSig] = useState(null);
  const [editingSig, setEditingSig] = useState(false);
  const { data: sigStatus } = useQuery({
    queryKey: ["manager-signature"],
    queryFn: async () => (await api.get("/manager-signature")).data,
  });
  const hasSig = !!sigStatus?.has_signature;

  const saveManagerSig = async () => {
    if (!managerSig) return toast.error("Σχεδιάστε την υπογραφή σας");
    try {
      await api.put("/manager-signature", { signature: managerSig });
      await qc.invalidateQueries({ queryKey: ["manager-signature"] });
      setEditingSig(false);
      setManagerSig(null);
      toast.success("Η υπογραφή διαχειριστή αποθηκεύτηκε");
    } catch (e) {
      toast.error("Σφάλμα αποθήκευσης");
    }
  };

  const openDialog = (r) => { setSel(r); setPrice(String(r.special_price ?? r.amount ?? "")); setEditingSig(false); };

  const status = (d) => {
    if (d == null) return { label: "—", cls: "bg-secondary text-foreground" };
    if (d < 0) return { label: "Έληξε", cls: "bg-red-100 text-red-700" };
    if (d <= 30) return { label: `Λήγει σε ${d} ημ.`, cls: "bg-amber-100 text-amber-700" };
    return { label: `Ενεργή (${d} ημ.)`, cls: "bg-emerald-100 text-emerald-700" };
  };

  const renew = async (r) => {
    try {
      await api.post(`/contracts/${r.id}/renew`);
      await qc.invalidateQueries({ queryKey: ["contracts"] });
      qc.invalidateQueries({ queryKey: ["boats"] });
      qc.invalidateQueries({ queryKey: ["slots"] });
      toast.success("Η σύμβαση ανανεώθηκε κατά ένα έτος");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα ανανέωσης");
    }
  };

  const downloadPdf = () => {
    const token = localStorage.getItem("token");
    window.open(`${API}/contracts/${sel.id}.pdf?price=${encodeURIComponent(price || 0)}&token=${token}`, "_blank");
  };

  const sendEmail = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/contracts/${sel.id}/send`, { price: Number(price || 0), base: API });
      toast.success(`Το συμφωνητικό στάλθηκε στο ${res.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα αποστολής");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Ανανεώσεις – Συμβάσεις" subtitle="Ετήσιες συμβάσεις φιλοξενίας, χρονολογικά κατά ημερομηνία λήξης" />
      <div className="p-6">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Φόρτωση…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-sm border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="renewals-empty">
            Δεν υπάρχουν σκάφη με ετήσια σύμβαση.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-sm border border-border">
            <table className="w-full text-sm" data-testid="renewals-table">
              <thead className="bg-secondary/60 text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Σκάφος</th>
                  <th className="px-4 py-3 text-left">Ιδιοκτήτης</th>
                  <th className="px-4 py-3 text-left">Θέση</th>
                  <th className="px-4 py-3 text-left">Λήξη</th>
                  <th className="px-4 py-3 text-left">Κατάσταση</th>
                  <th className="px-4 py-3 text-right">Ενέργειες</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const st = status(r.days_left);
                  return (
                    <tr key={r.id} className="border-t border-border" data-testid={`renewal-row-${r.id}`}>
                      <td className="px-4 py-3 font-medium">
                        {r.name}
                        {r.renewal_requested && (
                          <span className="ml-2 rounded-sm bg-[#0055FF]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#0044CC]" data-testid={`renewal-flag-${r.id}`}>Ζήτησε ανανέωση</span>
                        )}
                      </td>
                      <td className="px-4 py-3">{r.owner}</td>
                      <td className="px-4 py-3">{r.slot_label || `#${r.slot_number}`}</td>
                      <td className="px-4 py-3 tabular-nums">{gr(r.end_date)}</td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${st.cls}`}>{st.label}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => renew(r)} data-testid={`renew-btn-${r.id}`}>
                            <ArrowsClockwise size={15} weight="bold" /> Ανανέωση +1 έτος
                          </Button>
                          <Button size="sm" className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={() => openDialog(r)} data-testid={`contract-btn-${r.id}`}>
                            <FileText size={15} weight="bold" /> Συμφωνητικό
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="contract-dialog">
          <DialogHeader>
            <DialogTitle>Συμφωνητικό φιλοξενίας — {sel?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-muted-foreground">Ετήσιο τίμημα (€)</Label>
              <Input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 rounded-sm" data-testid="contract-price" />
            </div>
            <p className="text-xs text-muted-foreground">Το τίμημα και τα φορολογικά στοιχεία της εγκατάστασης θα εμφανιστούν στο συμφωνητικό.</p>
            {!sel?.customer_email && <p className="text-xs text-amber-600">Δεν υπάρχει email πελάτη για αποστολή.</p>}
            <div className="rounded-sm border border-border p-3">
              <Label className="text-xs font-semibold text-muted-foreground">Υπογραφή διαχειριστή (Α΄ Συμβαλλόμενος)</Label>
              {hasSig && !editingSig ? (
                <div className="mt-2 flex items-center justify-between text-sm" data-testid="manager-sign-status">
                  <span className="font-medium text-emerald-600">✓ Έχει οριστεί — θα εμφανίζεται στο συμφωνητικό</span>
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setEditingSig(true)} data-testid="manager-sign-change">Αλλαγή</Button>
                </div>
              ) : (
                <div className="mt-2">
                  <p className="mb-1 text-xs text-muted-foreground">Σχεδιάστε την υπογραφή σας. Θα εμφανίζεται υπογεγραμμένη στο συμφωνητικό πριν σταλεί στον πελάτη.</p>
                  <SignaturePad onChange={setManagerSig} testId="manager-sign" />
                  <Button size="sm" className="mt-1 rounded-sm bg-[#10B981] hover:bg-[#0e9d70]" onClick={saveManagerSig} disabled={!managerSig} data-testid="manager-sign-save">Αποθήκευση υπογραφής</Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={downloadPdf} data-testid="contract-download">
              <FileText size={16} weight="bold" /> Λήψη PDF
            </Button>
            <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={sendEmail} disabled={busy || !sel?.customer_email || !hasSig} data-testid="contract-send">
              <PaperPlaneTilt size={16} weight="bold" /> Αποστολή στον πελάτη
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
